// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { Auth0DecodedHash, Auth0Error, CheckSessionOptions, WebAuth } from 'auth0-js';
import { Observable, Observer } from 'rxjs';
import { LoggerService } from './logger.service';
import { Token, OIDCUser } from '../state';
import { getAuthURLFromWindow } from '../utilities';
import { environment } from '../../../environments/environment';

export const WEB_AUTH_TOKEN = new InjectionToken<WebAuth>('webauth');

// Standard scopes, gives us name + email info.
const OPENID_CONNECT_SCOPES = 'openid profile email';

export class AuthCallbackResult {
  constructor(public readonly token: Token, public readonly redirectPath: any[]) {}
}

@Injectable()
export class AuthService {
  constructor(@Inject(WEB_AUTH_TOKEN) private webAuth: WebAuth, private loggerService: LoggerService) {}

  logout() {
    this.webAuth.logout({ returnTo: document.location.origin });
  }

  /**
   * @param requestID A unique and unguessable identifier for the request. It is used to validate that the incomming
   * authentication callback was sent from the current client. It should be randomly generated, and stored between page loads.
   */
  public authenticateWithTargetRoute(requestId: string, targetRoute: any[]) {
    const options = this.createAuthorizeOptions(targetRoute, requestId);
    this.webAuth.authorize(options);
  }

  public refreshAuthToken(requestId: string): Observable<Token> {
    const options = this.createCheckSessionOptions(requestId);
    return Observable.create((observer: Observer<Token>) => {
      this.webAuth.checkSession(options, (error, decodedHash) => {
        const authInfo = this.getAuthCallbackResult(error, decodedHash, requestId);
        if (authInfo !== undefined) {
          observer.next(authInfo.token);
        } else {
          observer.error(new Error('Failed to load token.'));
        }
        observer.complete();
      });
    });
  }

  public resetPassword(user: OIDCUser): Observable<null> {
    const options = this.createResetPasswordOptions(user);
    return Observable.create((observer: Observer<null>) => {
      this.webAuth.changePassword(options, (error: Auth0Error | null) => {
        if (error !== null) {
          observer.error(new Error('Failed to reset password.'));
        }
        observer.next(null);
        observer.complete();
      });
    });
  }

  private getAuthCallbackResult(error: Auth0Error | null, decodedHash: Auth0DecodedHash, requestId: string) {
    if (error) {
      this.loggerService.error(`Failed to load token with error: ${JSON.stringify(error)}`);
      return;
    }
    const authResult = this.readAuthCallbackResult(decodedHash);
    if (authResult === undefined) {
      this.loggerService.error(`Failed to load token, invalid result ${JSON.stringify(decodedHash)}`);
      return;
    }
    if (authResult.requestId !== requestId) {
      this.loggerService.error(`Failed to validate token. RequestId ${authResult.requestId} didn't match ${requestId}`);
      return;
    }
    return { token: authResult.token, redirectPath: authResult.redirectPath };
  }

  private readAuthCallbackResult(decodedHash: Auth0DecodedHash) {
    const token = this.readTokenFromDecodedHash(decodedHash);
    const state = this.readStateFromDecodedHash(decodedHash);
    if (token === undefined || state === undefined) {
      return;
    }
    return { token, redirectPath: state.redirectPath, requestId: state.requestId };
  }

  private readTokenFromDecodedHash(decodedHash: Auth0DecodedHash): Token | undefined {
    const { accessToken, idToken, expiresIn } = decodedHash;
    if (accessToken === undefined || idToken === undefined || expiresIn === undefined) {
      return;
    }
    return {
      accessToken: accessToken,
      idToken: idToken,
      expiresAt: new Date(expiresIn * 1000 + new Date().getTime())
    };
  }

  private readStateFromDecodedHash(decodedHash: Auth0DecodedHash) {
    const { state } = decodedHash;
    if (state === undefined) {
      return;
    }
    return this.decodeState(state);
  }

  private encodeState(requestId: string, redirectPath?: any[]): string {
    const obj = {
      redirectPath,
      requestId
    };
    return encodeURIComponent(JSON.stringify(obj));
  }

  private decodeState(state: string) {
    const obj = JSON.parse(decodeURIComponent(state));
    if (obj.requestId === undefined) {
      return;
    }
    const requestId = obj.requestId as string;
    const redirectPath = obj.redirectPath as any[] | undefined;
    return { redirectPath, requestId };
  }

  private createCheckSessionOptions(requestId: string): CheckSessionOptions {
    return {
      redirectUri: getAuthURLFromWindow(),
      state: this.encodeState(requestId),
      scope: OPENID_CONNECT_SCOPES
    };
  }

  private createAuthorizeOptions(targetRoute: any[], requestId: string) {
    return {
      redirectUri: getAuthURLFromWindow(),
      state: this.encodeState(`${requestId}`, targetRoute),
      scope: OPENID_CONNECT_SCOPES
    };
  }

  private createResetPasswordOptions(user: OIDCUser) {
    return {
      email: user.email,
      connection: this.getConnection()
    };
  }

  private getConnection() {
    return environment.production ? 'Username-Password-Authentication' : 'Dev-User-Database';
  }
}



// WEBPACK FOOTER //
// ./src/app/core/services/auth.service.ts